import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import { adminItemKey } from '../_shared/queries';
import { adminItemsKey } from './queries';

/**
 * The three publish transitions — and **all three of them live here, on the
 * list, and nowhere else**.
 *
 * A state transition is one decision about one title. Spreading it across the
 * list, the authoring form, and the edit surface would mean three
 * implementations of one rule, three places to keep the confirmation copy in
 * step, and an operator who has to remember which surface they last used. The
 * list is also where the operator already is when they think about state — the
 * whole page is titles sorted by what state they are in.
 *
 * **No mutation auto-retries and none is optimistic.** A publish is visible to
 * every viewer the instant it lands; a row that renders as published before the
 * server agreed is a claim the operator cannot check.
 *
 * The invalidation set carries the **content report** as well as the list: a
 * publish moves a figure on the console entry, and a dashboard still counting
 * the old state is drift nobody notices.
 */

function usePublishInvalidation() {
  const queryClient = useQueryClient();

  return (contentItemId: string) => {
    void queryClient.invalidateQueries({ queryKey: adminItemsKey() });
    void queryClient.invalidateQueries({ queryKey: adminItemKey(contentItemId) });
    void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'content') });
  };
}

export function usePublishContentItem() {
  const invalidate = usePublishInvalidation();

  return useMutation({
    mutationFn: (contentItemId: string) =>
      api.post<null>('content', `/admin/contentItems/${contentItemId}:publish`),
    onSuccess: (_data, contentItemId) => invalidate(contentItemId),
    retry: false,
  });
}

export function useUnpublishContentItem() {
  const invalidate = usePublishInvalidation();

  return useMutation({
    mutationFn: (contentItemId: string) =>
      api.post<null>('content', `/admin/contentItems/${contentItemId}:unpublish`),
    onSuccess: (_data, contentItemId) => invalidate(contentItemId),
    retry: false,
  });
}
